import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Work as PositionIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { positionService, seniorityLevelService } from '../services/api';
import RichTextEditor from './RichTextEditor';
import './PositionManagement.css';
import { useConfirm } from './Common/ConfirmDialog';

const PositionManagement = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [seniorityLevels, setSeniorityLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    level: 'Mid'
  });
  const [isCodeManual, setIsCodeManual] = useState(false);

  // Load positions
  useEffect(() => {
    loadPositions();
    loadSeniorityLevels();
  }, []);

  const loadPositions = async () => {
    try {
      setLoading(true);
      const data = await positionService.getAll();
      
      console.log('Positions API Response:', data);
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setPositions(data);
      } else {
        console.warn('API returned non-array data:', data);
        setPositions([]);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load positions');
      console.error('Error loading positions:', err);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSeniorityLevels = async () => {
    try {
      const data = await seniorityLevelService.getAll();
      setSeniorityLevels(data);
    } catch (error) {
      console.error('Error loading seniority levels:', error);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      code: '',
      title: '',
      description: '',
      level: 'Mid'
    });
    setIsCodeManual(false);
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '',
      title: item.title || '',
      description: item.description || '',
      level: item.level || 'Mid'
    });
    setIsCodeManual(true); // Assume existing codes are manual
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ message: 'Are you sure you want to delete this position?', danger: true, confirmLabel: 'Delete' }))) {
      return;
    }

    try {
      await positionService.delete(id);
      setSuccess('Position deleted successfully');
      loadPositions();
    } catch (err) {
      setError('Failed to delete position');
      console.error('Error deleting position:', err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await positionService.update(editingItem.id, formData);
        setSuccess('Position updated successfully');
      } else {
        await positionService.create(formData);
        setSuccess('Position created successfully');
      }
      
      setDialogOpen(false);
      loadPositions();
    } catch (err) {
      setError('Failed to save position');
      console.error('Error saving position:', err);
    }
  };

  const handleInputChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    // Handle manual code editing
    if (field === 'code') {
      setIsCodeManual(true);
    }

    // Auto-generate code when level or title changes (only if not manually edited)
    if ((field === 'level' || field === 'title') && !isCodeManual) {
      const level = field === 'level' ? value : formData.level;
      const title = field === 'title' ? value : formData.title;

      if (level && title) {
        newFormData.code = generatePositionCode(level, title);
      }
    }

    setFormData(newFormData);
  };

  // Function to regenerate code manually
  const handleRegenerateCode = () => {
    if (formData.level && formData.title) {
      const newCode = generatePositionCode(formData.level, formData.title);
      setFormData(prev => ({ ...prev, code: newCode }));
      setIsCodeManual(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      'Junior': '#ff9800',
      'Mid': '#2196f3',
      'Senior': '#4caf50',
      'Lead': '#9c27b0',
      'Principal': '#f44336'
    };
    return colors[level] || '#757575';
  };

  // Function to strip HTML and get plain text for display
  const getPlainText = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Generate unique position code based on Level + Title
  const generatePositionCode = (level, title) => {
    if (!level || !title) return '';

    // Get level abbreviation
    const levelMap = {
      'Junior': 'JUN',
      'Mid': 'MID',
      'Senior': 'SEN',
      'Lead': 'LEA',
      'Principal': 'PRI'
    };

    // Get title abbreviation (first 3 letters of each word, max 3 words)
    const titleWords = title.trim().split(/\s+/).slice(0, 3);
    const titleAbbr = titleWords
      .map(word => word.substring(0, 3).toUpperCase())
      .join('');

    const levelAbbr = levelMap[level] || level.substring(0, 3).toUpperCase();
    const baseCode = `${levelAbbr}-${titleAbbr}`;

    // Find next available number
    let counter = 1;
    let finalCode = `${baseCode}-${counter.toString().padStart(3, '0')}`;

    // Check for uniqueness against existing positions
    while (positions.some(pos => pos.code === finalCode && pos.id !== editingItem?.id)) {
      counter++;
      finalCode = `${baseCode}-${counter.toString().padStart(3, '0')}`;
    }

    return finalCode;
  };

  return (
    <Box className="position-management-container">
      {/* Header with Breadcrumbs */}
      <Box className="position-header">
        <Breadcrumbs className="breadcrumbs">
          <Link 
            color="inherit" 
            onClick={() => navigate('/settings')}
            className="breadcrumb-link"
          >
            Settings
          </Link>
          <Typography color="text.primary">Positions</Typography>
        </Breadcrumbs>
        
        <Box className="header-content">
          <Box className="title-section">
            <PositionIcon className="header-icon" />
            <Box>
              <Typography variant="h4" className="page-title">
                Job Positions
              </Typography>
              <Typography variant="body1" className="page-subtitle">
                Manage job positions and titles across the organization
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            className="add-button"
          >
            Add Position
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} className="alert">
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} className="alert">
          {success}
        </Alert>
      )}

      {/* Content */}
      <Card className="content-card">
        <CardContent>
          <TableContainer component={Paper} className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Employees</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : !Array.isArray(positions) || positions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No positions found
                    </TableCell>
                  </TableRow>
                ) : (
                  positions.map((item) => (
                    <TableRow key={item.id} className="table-row">
                      <TableCell>
                        <Typography variant="body2" className="code-cell">
                          {item.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" className="title-cell">
                          {item.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            <div
                              dangerouslySetInnerHTML={{
                                __html: item.description || 'No description'
                              }}
                              style={{ maxWidth: '400px' }}
                            />
                          }
                          arrow
                        >
                          <Typography variant="body2" className="description-cell">
                            {getPlainText(item.description)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.level || 'Mid'}
                          size="small"
                          style={{
                            backgroundColor: getLevelColor(item.level || 'Mid'),
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.employeeCount || 0}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(item)}
                          className="action-button edit-button"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item.id)}
                          className="action-button delete-button"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          {editingItem ? 'Edit Position' : 'Add New Position'}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <Box className="dialog-form">
            {/* Top Row - Code and Title */}
            <Box className="form-row">
              <Box sx={{ flex: 1, marginRight: 2, position: 'relative' }}>
                <TextField
                  label="Position Code"
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  margin="normal"
                  required
                  placeholder="Auto-generated from Level + Title"
                  fullWidth
                  helperText={isCodeManual ? "Manually edited" : "Auto-generated"}
                  InputProps={{
                    style: {
                      fontFamily: 'monospace',
                      backgroundColor: isCodeManual ? 'white' : '#f8f9fa'
                    }
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRegenerateCode}
                  disabled={!formData.level || !formData.title}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1
                  }}
                  title="Regenerate code from Level + Title"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                label="Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                margin="normal"
                required
                placeholder="e.g., Software Developer"
                sx={{ flex: 2 }}
              />
            </Box>

            {/* Second Row - Department and Level */}
            <Box className="form-row">
              <Box sx={{ display:'none' }} />
              <TextField
                label="Level"
                value={formData.level}
                onChange={(e) => handleInputChange('level', e.target.value)}
                margin="normal"
                select
                SelectProps={{ native: true }}
                sx={{ flex: 1 }}
              >
                <option value="">Select Level</option>
                {seniorityLevels.map((level) => (
                  <option key={level.id} value={level.name}>
                    {level.name}
                  </option>
                ))}
              </TextField>
            </Box>

            {/* Description - Full Width */}
            <Box className="description-section">
              <RichTextEditor
                label="Description"
                value={formData.description}
                onChange={(value) => handleInputChange('description', value)}
                placeholder="Job responsibilities and requirements..."
                maxLength={1500}
                helperText="Use formatting tools to structure the job description"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PositionManagement;
